import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sailboat, Plus, Trash2, Users, CalendarDays, FileSpreadsheet, Send, Recycle } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { ALLERGENS } from "../lib/allergens";

const gInit = (g) => ((g.first_name?.[0] || "") + (g.last_name?.[0] || "")).toUpperCase() || (g.name?.[0] || "?").toUpperCase();
const aName = (id) => ALLERGENS.find((a) => a.id === id)?.name || id;

const empty = { name: "", client: "", start_date: "", end_date: "", guests: 0, budget: 0, notes: "" };

export default function Charter() {
  const { canWrite, user } = useAuth();
  const isMarpolUser = user?.role === "chef" || user?.role === "comandante";
  const [items, setItems] = useState([]);
  const [guests, setGuests] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/charters").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/guests").then((r) => setGuests(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      toast.error("Compila nome e date");
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error("La data di fine è prima dell'inizio");
      return;
    }
    setSaving(true);
    try {
      await api.post("/charters", { ...form, guests: Number(form.guests) || 0, budget: Number(form.budget) || 0 });
      toast.success("Charter salvato");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il charter "${c.name}"?`)) return;
    try { await api.delete(`/charters/${c.id}`); load(); } catch { /* noop */ }
  };

  const days = (a, b) => (a && b ? Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1) : 0);
  const eur = (n) => `€${(n || 0).toLocaleString("it-IT")}`;

  const downloadReport = async (c) => {
    try {
      const res = await api.get(`/charters/${c.id}/report.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `charter-${(c.name || "report").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  const sendReport = async (c) => {
    try {
      const { data } = await api.post(`/charters/${c.id}/send-report`);
      toast.success(`Report inviato a ${data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
  };

  const registerWaste = async (c) => {
    const covers = c.stats?.covers || 0;
    const m3 = +(covers * 0.0025).toFixed(3);
    const kg = +(covers * 0.35).toFixed(1);
    try {
      await api.post("/marpol/inventory/adjust", { waste_category: "B_food_waste", delta_m3: m3, delta_kg: kg, storage_location: `Charter ${c.name}` });
      toast.success(`Registrati +${m3} m³ (${kg} kg) di rifiuti alimentari dal charter "${c.name}"`);
    } catch (e) { toast.error(e.response?.data?.detail || "Registrazione rifiuti non riuscita"); }
  };

  return (
    <div className="space-y-6" data-testid="charter-page">
      <div>
        <h1 className="font-head text-3xl font-bold text-navy">Charter Planner</h1>
        <p className="mt-1 text-sm text-slate-500">Ogni charter calcola in automatico spesa e food cost dai giorni del periodo.</p>
      </div>

      {canWrite && (
        <form onSubmit={save} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-6 md:grid-cols-3">
          <input data-testid="charter-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome charter" className="h-11 rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
          <input data-testid="charter-client" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="Cliente (opzionale)" className="h-11 rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
          <input data-testid="charter-guests" type="number" min="0" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} placeholder="Ospiti" className="h-11 rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
          <label className="text-xs text-slate-500">Inizio<input data-testid="charter-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 h-11 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" /></label>
          <label className="text-xs text-slate-500">Fine<input data-testid="charter-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 h-11 w-full rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" /></label>
          <input data-testid="charter-budget" type="number" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="Budget € (opzionale)" className="h-11 rounded-lg border-2 border-slate-200 px-3 outline-none focus:border-gold" />
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note" rows={1} className="md:col-span-2 rounded-lg border-2 border-slate-200 px-3 py-2 outline-none focus:border-gold" />
          <button data-testid="charter-save" type="submit" disabled={saving} className="flex h-11 items-center justify-center gap-2 rounded-lg bg-navy font-semibold text-white hover:bg-navy-hover disabled:opacity-60"><Plus className="h-4 w-4" /> Aggiungi charter</button>
        </form>
      )}

      {items.length > 0 && (() => {
        const today = new Date().toISOString().slice(0, 10);
        const statusOf = (c) => (c.end_date < today ? "Passato" : c.start_date > today ? "Prossimo" : "In corso");
        const palette = ["#0B132B", "#D4AF37", "#1E3A5F", "#C99A2E", "#2E5E4E", "#8A6D1F", "#3B5BA9", "#B08D57"];
        const pieData = items
          .map((c) => ({ name: c.name, status: statusOf(c), value: (c.stats && c.stats.total_spend) || 0 }))
          .filter((d) => d.value > 0);
        if (pieData.length === 0) return null;
        const spesePassati = pieData.filter((d) => d.status === "Passato").reduce((a, b) => a + b.value, 0);
        const speseAttivi = pieData.filter((d) => d.status !== "Passato").reduce((a, b) => a + b.value, 0);
        return (
          <div data-testid="charter-pie-card" className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="font-head text-lg font-semibold text-navy">Confronto spesa charter</h2>
            <p className="mb-4 text-sm text-slate-500">Quota di spesa per ogni charter — passati a confronto con quelli in corso e prossimi.</p>
            <div className="grid items-center gap-4 md:grid-cols-2">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => e.name}>
                    {pieData.map((d, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n, p) => [eur(v), `${p.payload.name} (${p.payload.status})`]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-lg bg-[#F4F1EA] px-4 py-3">
                  <span className="text-sm font-semibold text-navy">Charter passati</span>
                  <span data-testid="charter-past-total" className="font-head font-bold text-navy">{eur(spesePassati)}</span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-gold/15 px-4 py-3">
                  <span className="text-sm font-semibold text-navy">In corso / prossimi</span>
                  <span data-testid="charter-active-total" className="font-head font-bold text-navy">{eur(speseAttivi)}</span>
                </div>
                <div className="mt-2 space-y-1">
                  {pieData.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-slate-600">
                        <span className="inline-block h-3 w-3 rounded-full" style={{ background: palette[i % palette.length] }} /> {d.name}
                        <span className="text-xs text-slate-400">· {d.status}</span>
                      </span>
                      <span className="font-medium text-navy">{eur(d.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="grid gap-4 md:grid-cols-2">
        {items.length === 0 && <div className="rounded-2xl bg-white p-8 text-center text-slate-400" data-testid="charter-empty">Nessun charter ancora.</div>}
        {items.map((c) => {
          const s = c.stats || {};
          const over = c.budget > 0 && s.total_spend > c.budget;
          return (
            <div key={c.id} data-testid={`charter-card-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-gold"><Sailboat className="h-5 w-5" /></div>
                  <div>
                    <div className="font-head text-lg font-bold text-navy">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.client || "—"}</div>
                  </div>
                </div>
                {canWrite && <button data-testid={`charter-delete-${c.id}`} onClick={() => remove(c)} className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {c.start_date} → {c.end_date} ({days(c.start_date, c.end_date)}g)</span>
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {c.guests} ospiti</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-[#F4F1EA] p-3"><div className="text-[11px] text-slate-500">Spesa totale</div><div className="font-head text-lg font-bold text-navy">{eur(s.total_spend)}</div></div>
                <div className="rounded-lg bg-[#F4F1EA] p-3"><div className="text-[11px] text-slate-500">Food cost/coperto</div><div className="font-head text-lg font-bold text-navy">{eur(s.food_cost_per_cover)}</div></div>
                <div className="rounded-lg bg-[#F4F1EA] p-3"><div className="text-[11px] text-slate-500">Coperti</div><div className="font-head text-lg font-bold text-navy">{s.covers || 0}</div></div>
              </div>
              {s.covers > 0 && isMarpolUser && (
                <div data-testid={`charter-waste-${c.id}`} className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm">
                  <span className="text-emerald-800">Scarti alimentari stimati (0,35 kg/coperto): <b>{(s.covers * 0.35).toFixed(1)} kg</b> · <b>{(s.covers * 0.0025).toFixed(3)} m³</b></span>
                  {canWrite && <button data-testid={`charter-waste-btn-${c.id}`} onClick={() => registerWaste(c)} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"><Recycle className="h-3.5 w-3.5" /> Registra nei rifiuti MARPOL</button>}
                </div>
              )}
              {(() => {
                const aboard = guests.filter((g) => (g.charter_ids || []).includes(c.id));
                if (aboard.length === 0) return null;
                const allg = [...new Set(aboard.flatMap((g) => g.allergen_ids || []))];
                return (
                  <div className="mt-4 border-t border-slate-100 pt-3" data-testid={`charter-guests-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase text-slate-400">Ospiti a bordo ({aboard.length})</span>
                      {allg.length > 0 && <span className="text-xs font-semibold text-red-600">{allg.length} allergeni da evitare</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {aboard.map((g) => (
                        <span key={g.id} className="flex items-center gap-1.5 rounded-full bg-[#F4F1EA] py-1 pl-1 pr-2.5 text-xs text-navy">
                          {g.photo_url ? <img src={g.photo_url} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="flex h-5 w-5 items-center justify-center rounded-full bg-navy text-[9px] font-bold text-gold">{gInit(g)}</span>}
                          {g.name}{g.severe_allergy ? " 🥜" : ""}
                        </span>
                      ))}
                    </div>
                    {allg.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {allg.map((id) => <span key={id} className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">{aName(id)}</span>)}
                      </div>
                    )}
                  </div>
                );
              })()}
              {c.budget > 0 && (
                <div className={`mt-3 rounded-lg px-3 py-2 text-sm ${over ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                  Budget {eur(c.budget)} · {over ? `Superato di ${eur(s.total_spend - c.budget)}` : `Rimanente ${eur(c.budget - (s.total_spend || 0))}`}
                </div>
              )}
              {c.notes && <p className="mt-3 text-sm text-slate-500">{c.notes}</p>}
              <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                <button data-testid={`charter-report-${c.id}`} onClick={() => downloadReport(c)} className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-navy hover:bg-slate-200">
                  <FileSpreadsheet className="h-4 w-4" /> Report Excel
                </button>
                {canWrite && (
                  <button data-testid={`charter-send-${c.id}`} onClick={() => sendReport(c)} className="flex items-center gap-2 rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white hover:bg-navy-hover">
                    <Send className="h-4 w-4" /> Invia all'armatore
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
